import { assertValidSupportAIRunCursor } from '@/utils/support-ai-run';

const supportPathSegment = (value: string | number) => encodeURIComponent(String(value));

export type SupportAIReleaseApprovalOperation =
  | 'approve'
  | 'reject'
  | 'cancel'
  | 'escalate'
  | 're_request';

const SUPPORT_AI_RELEASE_APPROVAL_OPERATIONS: readonly SupportAIReleaseApprovalOperation[] = [
  'approve',
  'reject',
  'cancel',
  'escalate',
  're_request'
];

const SUPPORT_AI_RELEASE_APPROVAL_SEGMENTS = {
  approve: 'approve',
  reject: 'reject',
  cancel: 'cancel',
  escalate: 'escalate',
  re_request: 're-request'
} as const satisfies Record<SupportAIReleaseApprovalOperation, string>;

function assertSupportAIReleaseApprovalOperation(
  operation: SupportAIReleaseApprovalOperation
): void {
  if (!SUPPORT_AI_RELEASE_APPROVAL_OPERATIONS.includes(operation)) {
    throw new TypeError('Unsupported Support AI release approval operation');
  }
}

export const supportApiPaths = {
  userProfile: () => '/api/support/user/profile/',
  userAddresses: () => '/api/support/user/addresses/',
  userAddress: (addressId: number) => `/api/support/user/addresses/${supportPathSegment(addressId)}/`,
  fileUpload: () => '/api/support/file-upload/',
  fileUploadItem: (fileId: string | number) => `/api/support/file-upload/${supportPathSegment(fileId)}/`,
  analytics: () => '/api/support/analytics/',
  dashboardStats: () => '/api/support/dashboard/stats/',
  roles: () => '/api/support/roles/',
  supportAgents: () => '/api/support/roles/agents/',
  supportAdmins: () => '/api/support/roles/admins/',
  tickets: () => '/api/support/tickets/',
  ticket: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/`,
  ticketAssign: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/assign/`,
  ticketChangeStatus: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/change-status/`,
  ticketChangePriority: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/change-priority/`,
  ticketClose: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/close/`,
  ticketReopen: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/reopen/`,
  ticketMarkViewed: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/mark-viewed/`,
  ticketReplies: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/replies/`,
  ticketAIConversations: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/conversations/`,
  ticketAIConversation: (ticketNumber: string, conversationId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/conversations/${supportPathSegment(conversationId)}/`,
  ticketAIConversationMessages: (ticketNumber: string, conversationId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/conversations/${supportPathSegment(conversationId)}/messages/`,
  ticketAISuggestions: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/suggestions/`,
  ticketAIFeedback: (ticketNumber: string, messageId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/messages/${supportPathSegment(messageId)}/feedback/`,
  ticketAIUsage: (ticketNumber: string, messageId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/messages/${supportPathSegment(messageId)}/usage/`,
  ticketAIRuns: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/runs/`,
  ticketAIRun: (ticketNumber: string, runId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/runs/${supportPathSegment(runId)}/`,
  ticketAIRunEvents: (ticketNumber: string, runId: string, query?: { cursor?: number; limit?: number }) => {
    const path = `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/runs/${supportPathSegment(runId)}/events/`;
    const params = new URLSearchParams();
    if (typeof query?.cursor === 'number') {
      assertValidSupportAIRunCursor(query.cursor);
      params.set('cursor', String(query.cursor));
    }
    if (typeof query?.limit === 'number') params.set('limit', String(query.limit));
    return params.size ? `${path}?${params.toString()}` : path;
  },
  ticketAIRunEventStream: (ticketNumber: string, runId: string, cursor?: number) => {
    const path = `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/runs/${supportPathSegment(runId)}/events/stream/`;
    if (typeof cursor !== 'number') return path;
    assertValidSupportAIRunCursor(cursor);
    return `${path}?cursor=${encodeURIComponent(String(cursor))}`;
  },
  ticketAIRunCancel: (ticketNumber: string, runId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/runs/${supportPathSegment(runId)}/cancel/`,
  ticketAICustomerStatus: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/customer-status/`,
  ticketAICustomerFeedback: (ticketNumber: string, responseId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/customer-responses/${supportPathSegment(responseId)}/feedback/`,
  ticketAIRequestHuman: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/ai/request-human/`,
  aiAssistantStatus: () => '/api/support/ai/assistant/status/',
  aiAssistantRecoveryPreview: () => '/api/support/ai/assistant/development-recovery/preview/',
  aiAssistantRecoveryConfirm: () => '/api/support/ai/assistant/development-recovery/confirm/',
  supportAIAnalytics: () => '/api/support/ai/analytics/',
  supportAIAuditConversations: () => '/api/support/ai/audit/conversations/',
  supportAIAuditConversation: (conversationId: string) =>
    `/api/support/ai/audit/conversations/${supportPathSegment(conversationId)}/`,
  supportAIAuditRuns: () => '/api/support/ai/audit/runs/',
  supportAIAuditRun: (runId: string) => `/api/support/ai/audit/runs/${supportPathSegment(runId)}/`,
  supportAIOperationsSummary: () => '/api/support/ai/operations/summary/',
  supportAIEvaluationSets: () => '/api/support/ai/governance/evaluation-sets/',
  supportAIEvaluationSet: (evaluationId: string) =>
    `/api/support/ai/governance/evaluation-sets/${supportPathSegment(evaluationId)}/`,
  supportAIReplayConfigurations: () => '/api/support/ai/governance/replay-configurations/',
  supportAIEvaluationReplays: () => '/api/support/ai/governance/replays/',
  supportAIEvaluationReplay: (replayId: string) =>
    `/api/support/ai/governance/replays/${supportPathSegment(replayId)}/`,
  supportAIEvaluationReplayStream: (replayId: string) =>
    `/api/support/ai/governance/replays/${supportPathSegment(replayId)}/stream/`,
  supportAIEvaluationReplayCancel: (replayId: string) =>
    `/api/support/ai/governance/replays/${supportPathSegment(replayId)}/cancel/`,
  supportAIReleases: () => '/api/support/ai/governance/releases/',
  supportAIRelease: (releaseId: string) =>
    `/api/support/ai/governance/releases/${supportPathSegment(releaseId)}/`,
  supportAIReleaseApproval: (
    releaseId: string,
    approvalId: number,
    operation: SupportAIReleaseApprovalOperation
  ) => {
    assertSupportAIReleaseApprovalOperation(operation);
    return `/api/support/ai/governance/releases/${supportPathSegment(releaseId)}/approvals/${supportPathSegment(approvalId)}/${SUPPORT_AI_RELEASE_APPROVAL_SEGMENTS[operation]}/`;
  },
  supportAIReleaseExecute: (releaseId: string) =>
    `/api/support/ai/governance/releases/${supportPathSegment(releaseId)}/execute/`,
  supportAIRollouts: () => '/api/support/ai/governance/rollouts/',
  supportAIRollout: (rolloutId: string) =>
    `/api/support/ai/governance/rollouts/${supportPathSegment(rolloutId)}/`,
  supportAIRolloutWatchdogCheckpoints: (rolloutId: string) =>
    `/api/support/ai/governance/rollouts/${supportPathSegment(rolloutId)}/watchdog-checkpoints/`,
  supportAIRolloutDevelopmentPrecondition: (rolloutId: string) =>
    `/api/support/ai/governance/rollouts/${supportPathSegment(rolloutId)}/development-precondition/`,
  supportAIRolloutControl: (rolloutId: string) =>
    `/api/support/ai/governance/rollouts/${supportPathSegment(rolloutId)}/control/`,
  supportAIRolloutRollback: (rolloutId: string) =>
    `/api/support/ai/governance/rollouts/${supportPathSegment(rolloutId)}/rollback/`,
  supportAICustomerReadiness: () => '/api/support/ai/governance/customer-readiness/',
  supportAICalibrationCurrentPolicy: () => '/api/support/ai/governance/calibration/policies/current/',
  supportAICalibrationManifests: () => '/api/support/ai/governance/calibration/manifests/',
  supportAICalibrationManifest: (manifestId: string) =>
    `/api/support/ai/governance/calibration/manifests/${supportPathSegment(manifestId)}/`,
  supportAICalibrationManifestFinalize: (manifestId: string) => `/api/support/ai/governance/calibration/manifests/${supportPathSegment(manifestId)}/finalize/`,
  supportAICalibrationReviewSessions: () => '/api/support/ai/governance/calibration/review-sessions/',
  supportAICalibrationReviewSessionNext: (sessionId: string) => `/api/support/ai/governance/calibration/review-sessions/${supportPathSegment(sessionId)}/next/`,
  supportAICalibrationReviewSessionLabel: (sessionId: string) => `/api/support/ai/governance/calibration/review-sessions/${supportPathSegment(sessionId)}/labels/`,
  supportAICalibrationReviewSessionCancel: (sessionId: string) => `/api/support/ai/governance/calibration/review-sessions/${supportPathSegment(sessionId)}/cancel/`,
  supportAICalibrationArtifacts: () => '/api/support/ai/governance/calibration/artifacts/',
  supportAICalibrationArtifact: (artifactId: string) =>
    `/api/support/ai/governance/calibration/artifacts/${supportPathSegment(artifactId)}/`,
  supportAICalibrationArtifactApprovals: (artifactId: string) => `/api/support/ai/governance/calibration/artifacts/${supportPathSegment(artifactId)}/approvals/`,
  supportAICalibrationArtifactCurrentApproval: (artifactId: string) => `/api/support/ai/governance/calibration/artifacts/${supportPathSegment(artifactId)}/approvals/current/`,
  supportAICalibrationArtifactApproval: (artifactId: string, approvalId: number, operation: 'approve' | 'reject' | 'cancel') => `/api/support/ai/governance/calibration/artifacts/${supportPathSegment(artifactId)}/approvals/${supportPathSegment(approvalId)}/${operation}/`,
  supportAICalibrationArtifactExecute: (artifactId: string) => `/api/support/ai/governance/calibration/artifacts/${supportPathSegment(artifactId)}/execute/`,
  supportAICalibrationPolicyActivationApprovals: (policyId: string) => `/api/support/ai/governance/calibration/policies/${supportPathSegment(policyId)}/activation-approvals/`,
  supportAICalibrationPolicyCurrentApproval: (policyId: string) => `/api/support/ai/governance/calibration/policies/${supportPathSegment(policyId)}/activation-approvals/current/`,
  supportAICalibrationNextPolicy: () => '/api/support/ai/governance/calibration/policies/next/',
  supportAICalibrationPolicyApproval: (policyId: string, approvalId: number, operation: 'approve' | 'reject' | 'cancel') => `/api/support/ai/governance/calibration/policies/${supportPathSegment(policyId)}/activation-approvals/${supportPathSegment(approvalId)}/${operation}/`,
  supportAICalibrationPolicyActivate: (policyId: string) => `/api/support/ai/governance/calibration/policies/${supportPathSegment(policyId)}/activate/`,
  ticketCollaborationInternalNote: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/collaboration/internal-note/`,
  ticketCollaborationWatchers: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/collaboration/watchers/`,
  ticketCollaborationAcknowledge: (ticketNumber: string, requestId: string | number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/collaboration/attention/${supportPathSegment(requestId)}/acknowledge/`,
  ticketCollaborationResolve: (ticketNumber: string, requestId: string | number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/collaboration/attention/${supportPathSegment(requestId)}/resolve/`,
  ticketCollaborationHandoff: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/collaboration/handoff/`,
  ticketActivity: (ticketNumber: string) => `/api/support/tickets/${supportPathSegment(ticketNumber)}/activity/`,
  ticketCustomerAdminOverview: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/overview/`,
  ticketCustomerAdminCredits: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/credits/`,
  ticketCustomerAdminCreditPackageExtend: (ticketNumber: string, packageId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/credit-packages/${supportPathSegment(packageId)}/extend/`,
  ticketCustomerAdminGrantExtend: (ticketNumber: string, grantId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/grants/${supportPathSegment(grantId)}/extend/`,
  ticketCustomerAdminApiKeys: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/api-keys/`,
  ticketCustomerAdminApiActivity: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/api-activity/`,
  ticketCustomerAdminPayments: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/payments/`,
  ticketCustomerAdminPaymentRecheck: (ticketNumber: string, paymentId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/payments/${supportPathSegment(paymentId)}/recheck/`,
  ticketCustomerAdminAffiliates: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/affiliates/`,
  ticketCustomerAdminReports: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/reports/`,
  ticketCustomerAdminReportDownload: (ticketNumber: string, fileId: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/reports/${supportPathSegment(fileId)}/download/`,
  ticketCustomerAdminGatewayBypass: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/gateway-bypass/`,
  ticketCustomerAdminSubscription: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/subscription/`,
  ticketCustomerAdminSubscriptionCancel: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/subscription/cancel/`,
  ticketCustomerAdminTickets: (ticketNumber: string) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/customer-admin/tickets/`,
  // Agent/admin customer directory search and user-scoped administration
  // (resolves the customer directly by id, independent of any ticket).
  customerAdminSearch: () => '/api/support/customer-admin/search/',
  userCustomerAdminOverview: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/overview/`,
  userCustomerAdminTickets: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/tickets/`,
  userCustomerAdminCredits: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/credits/`,
  userCustomerAdminCreditPackageExtend: (userId: string, packageId: number) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/credit-packages/${supportPathSegment(packageId)}/extend/`,
  userCustomerAdminGrantExtend: (userId: string, grantId: number) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/grants/${supportPathSegment(grantId)}/extend/`,
  userCustomerAdminApiKeys: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/api-keys/`,
  userCustomerAdminApiActivity: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/api-activity/`,
  userCustomerAdminPayments: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/payments/`,
  userCustomerAdminPaymentRecheck: (userId: string, paymentId: number) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/payments/${supportPathSegment(paymentId)}/recheck/`,
  userCustomerAdminAffiliates: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/affiliates/`,
  userCustomerAdminGatewayBypass: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/gateway-bypass/`,
  userCustomerAdminSubscription: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/subscription/`,
  userCustomerAdminSubscriptionCancel: (userId: string) =>
    `/api/support/customer-admin/users/${supportPathSegment(userId)}/subscription/cancel/`,
  ticketAttachmentDownload: (ticketNumber: string, attachmentId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/attachments/${supportPathSegment(attachmentId)}/download/`,
  ticketAttachmentRetryScan: (ticketNumber: string, attachmentId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/attachments/${supportPathSegment(attachmentId)}/retry-scan/`,
  ticketAttachmentScanStatus: (ticketNumber: string, attachmentId: number) =>
    `/api/support/tickets/${supportPathSegment(ticketNumber)}/attachments/${supportPathSegment(attachmentId)}/scan-status/`,
  ticketExport: () => '/api/support/tickets/export/',
  bulkAssignTickets: () => '/api/support/tickets/bulk-assign/',
  bulkChangeStatus: () => '/api/support/tickets/bulk-status/',
  bulkChangePriority: () => '/api/support/tickets/bulk-priority/',
  categories: () => '/api/support/categories/',
  categoryFields: (categoryId: number) => `/api/support/categories/${supportPathSegment(categoryId)}/fields/`,
  validateCategoryFields: (categoryId: number) =>
    `/api/support/categories/${supportPathSegment(categoryId)}/validate-fields/`,
  knowledgeBaseCategories: () => '/api/support/knowledge-base/categories/',
  knowledgeBaseArticles: () => '/api/support/knowledge-base/articles/',
  knowledgeBasePopular: () => '/api/support/knowledge-base/articles/popular/',
  knowledgeBaseSearch: () => '/api/support/knowledge-base/search/',
  knowledgeBaseArticle: (id: number) => `/api/support/knowledge-base/articles/${supportPathSegment(id)}/`,
  markArticleHelpful: (id: number) => `/api/support/knowledge-base/articles/${supportPathSegment(id)}/mark-helpful/`,
  markArticleNotHelpful: (id: number) =>
    `/api/support/knowledge-base/articles/${supportPathSegment(id)}/mark-not-helpful/`,
  cannedResponses: () => '/api/support/canned-responses/',
  replyTemplates: () => '/api/support/reply-templates/',
  useCannedResponse: (id: number) => `/api/support/canned-responses/${supportPathSegment(id)}/use/`,
  ticketTemplates: () => '/api/support/ticket-templates/',
  notificationPreferences: () => '/api/support/notification-preferences/',
  updateNotificationPreferences: () => '/api/support/notification-preferences/update/',
  resetNotificationPreferences: () => '/api/support/notification-preferences/reset/',
  toggleNotificationPreferences: () => '/api/support/notification-preferences/toggle/'
} as const;
